import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import styled from 'libs/styled';
import Image from '@fogia/core-web/components/Image';
import { above, below } from '@fogia/core-web/utils/mediaqueries';
import { show as showOverlay } from '@fogia/core-web/state/application/overlay';
import useAppDispatch from '@fogia/core-web/hooks/useAppDispatch';
import Text from 'components/Text';
import { ExpandIcon } from 'assets/media/icons/ExpandIcon';

interface ProductFile {
    id: string | number;
    code?: string;
    name?: string;
    format?: {
        image?: string;
    };
}

export interface ProductGalleryProps {
    files?: ProductFile[];
    mainImage?: string;
    productName?: string;
    productImages?: string[];
    text?: string;
    isWideProduct?: boolean;
}

// Full viewport container (accounting for StickyBar height: 38px desktop, 74px mobile)
const GalleryViewport = styled('div')`
    position: relative;
    width: 100vw;
    height: calc(100vh - 38px);
    height: calc(100dvh - 38px);
    background-color: var(--color-shade-light-beige, #f6f4ec);
    overflow: hidden;
    margin-left: calc(-50vw + 50%);

    ${below['tablet.lg']} {
        height: calc(100vh - 74px);
        height: calc(100dvh - 74px);
    }
`;

// Scroll snap container
const SlideContainer = styled('div')`
    display: flex;
    width: 100%;
    height: 100%;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

// Individual slide
const Slide = styled('div')`
    flex: 0 0 100%;
    width: 100vw;
    height: 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;
    position: relative;
`;

const SlideImageWrapper = styled('div')`
    position: absolute;
    top: 40px;
    left: 40px;
    right: 40px;
    bottom: 40px;

    /* Ensure Image component's wrappers fill the space */
    > div {
        width: 100%;
        height: 100%;
    }

    /* Force img to fill and be properly sized even when image fails to load */
    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    ${below['tablet.lg']} {
        top: 20px;
        left: 20px;
        right: 20px;
        bottom: 20px;
    }
`;

// Product info overlay - bottom left on desktop, centered below dots on mobile
const InfoOverlay = styled('div')`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 30;
    pointer-events: none;
    color: var(--ui-primary);
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(12, 1fr);
    padding: 0 10px 30px;

    ${above['tablet.lg']} {
        grid-template-columns: repeat(24, 1fr);
    }
`;

const InfoText = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 4px;
    grid-column: 1 / 13;
    pointer-events: auto;
`;

// Dot navigation - mobile only
const DotNav = styled('div')`
    display: none;

    ${below['tablet.lg']} {
        display: flex;
        position: absolute;
        bottom: 160px;
        left: 0;
        right: 0;
        justify-content: center;
        gap: 5px;
        z-index: 10;
    }
`;

// Individual dot indicator
const Dot = styled('button')<{ $isActive: boolean }>`
    width: 5px;
    height: 5px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: ${({ $isActive }) =>
        $isActive ? 'var(--color-ui-secondary, #e64b28)' : 'rgba(23, 23, 23, 0.15)'};
    cursor: pointer;
    transition: background-color var(--transition-primary-fast, 0.2s ease);

    &:focus {
        outline: 2px solid var(--color-base-black);
        outline-offset: 2px;
    }
`;

// Thumbnail navigation - desktop only
const ThumbnailNav = styled('div')`
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    gap: 10px;
    z-index: 10;

    ${below['tablet.lg']} {
        display: none;
    }
`;

const Thumbnail = styled('button')<{ $isActive: boolean }>`
    width: 50px;
    height: 63px;
    padding: 0;
    border: none;
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0.5)};
    cursor: pointer;
    overflow: hidden;
    background-color: var(--color-base-off-white);
    transition: opacity var(--transition-primary-fast, 0.2s ease);
    flex-shrink: 0;

    &:hover {
        opacity: ${({ $isActive }) => ($isActive ? 1 : 0.75)};
    }

    &:focus {
        outline: 2px solid var(--color-base-black);
        outline-offset: 2px;
    }

    /* Ensure img fills the button even when image fails to load */
    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    /* Force Image component wrapper to fill */
    > div {
        width: 100%;
        height: 100%;
    }

    ${below['tablet.lg']} {
        width: 40px;
        height: 50px;
    }
`;

// Expand button - positioned top right of the slide
const ExpandButton = styled('button')`
    position: absolute;
    top: 20px;
    right: 20px;
    width: 44px;
    height: 44px;
    padding: 0;
    background-color: var(--color-base-white);
    border: 1px solid var(--color-neutrals-200);
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: var(--color-neutrals-100);
    }

    &:focus {
        outline: 2px solid var(--color-primary-500, #e64b28);
        outline-offset: 2px;
    }

    ${below['tablet.lg']} {
        top: 12px;
        right: 12px;
        width: 40px;
        height: 40px;
    }
`;

// Empty state
const EmptyState = styled('div')`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--color-neutrals-500);
`;

const MAX_VISIBLE_THUMBNAILS = 4;

const ProductGallery = React.forwardRef<HTMLDivElement, ProductGalleryProps>(
    (
        { files = [], mainImage, productName = '', productImages = [], text = '', isWideProduct: isWide = false },
        ref,
    ) => {
        const dispatch = useAppDispatch();
        const [activeIndex, setActiveIndex] = useState(0);
        const slideContainerRef = useRef<HTMLDivElement>(null);
        const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
        const expandButtonRef = useRef<HTMLButtonElement>(null);
        const safeFiles = Array.isArray(files) ? files : [];

        const galleryImages = useMemo(() => {
            const images: Array<{ id: string | number; url: string; title: string }> = [];
            const existingUrls = new Set<string>();

            // Add main image first
            if (mainImage) {
                images.push({
                    id: 'main',
                    url: mainImage,
                    title: productName,
                });
                existingUrls.add(mainImage);
            }

            // Add additional images from files
            safeFiles.forEach((file) => {
                if (file.format?.image && !existingUrls.has(file.format.image)) {
                    existingUrls.add(file.format.image);
                    images.push({
                        id: file.id,
                        url: file.format.image,
                        title: file.name || productName,
                    });
                }
            });

            // If we have productImages from variants, add those too (filtered for unique URLs)
            if (productImages && productImages.length > 0) {
                productImages.forEach((imageUrl, index) => {
                    if (imageUrl && !existingUrls.has(imageUrl)) {
                        existingUrls.add(imageUrl);
                        images.push({
                            id: `variant-${index}`,
                            url: imageUrl,
                            title: productName,
                        });
                    }
                });
            }

            return images;
        }, [files, mainImage, productName, productImages]);

        // Calculate which thumbnails should be visible (sliding window of MAX_VISIBLE_THUMBNAILS)
        const visibleThumbnails = useMemo(() => {
            if (galleryImages.length <= MAX_VISIBLE_THUMBNAILS) {
                return galleryImages.map((img, idx) => ({
                    ...img,
                    originalIndex: idx,
                }));
            }

            // Calculate window start to keep active image visible (prefer active in middle when possible)
            const maxStart = galleryImages.length - MAX_VISIBLE_THUMBNAILS;
            const start = Math.max(0, Math.min(activeIndex - 1, maxStart));

            return galleryImages
                .slice(start, start + MAX_VISIBLE_THUMBNAILS)
                .map((img, idx) => ({ ...img, originalIndex: start + idx }));
        }, [galleryImages, activeIndex]);

        // Track scroll position and update active index
        useEffect(() => {
            const container = slideContainerRef.current;
            if (!container) return;

            const handleScroll = () => {
                const { scrollLeft, offsetWidth: slideWidth } = container;
                const newIndex = Math.round(scrollLeft / slideWidth);

                if (newIndex !== activeIndex && newIndex >= 0 && newIndex < galleryImages.length) {
                    setActiveIndex(newIndex);
                }
            };

            container.addEventListener('scroll', handleScroll, { passive: true });
            return () => container.removeEventListener('scroll', handleScroll);
        }, [activeIndex, galleryImages.length]);

        // Scroll to slide when thumbnail is clicked
        const scrollToSlide = useCallback((index: number) => {
            const container = slideContainerRef.current;
            if (!container) return;

            const slideWidth = container.offsetWidth;
            container.scrollTo({
                left: slideWidth * index,
                behavior: 'smooth',
            });
        }, []);

        // Open zoom gallery overlay
        const openZoomGallery = useCallback(() => {
            dispatch(
                showOverlay({
                    view: 'zoomGalleryOverlay',
                    transition: 'fade',
                    data: {
                        images: galleryImages,
                        startIndex: activeIndex,
                        productName,
                        triggerElement: expandButtonRef.current,
                        isWide,
                    },
                }),
            );
        }, [dispatch, galleryImages, activeIndex, productName, isWide]);

        // Handle keyboard navigation
        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'ArrowLeft' && activeIndex > 0) {
                    scrollToSlide(activeIndex - 1);
                } else if (e.key === 'ArrowRight' && activeIndex < galleryImages.length - 1) {
                    scrollToSlide(activeIndex + 1);
                }
            };

            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }, [activeIndex, galleryImages.length, scrollToSlide]);

        if (!galleryImages.length) {
            return (
                <GalleryViewport>
                    <EmptyState>No images available</EmptyState>
                </GalleryViewport>
            );
        }

        return (
            <GalleryViewport ref={ref}>
                {/* Expand button - top right */}
                <ExpandButton
                    ref={expandButtonRef}
                    onClick={openZoomGallery}
                    type="button"
                    aria-label="Open full screen gallery"
                >
                    <ExpandIcon />
                </ExpandButton>

                {/* Scrollable slides */}
                <SlideContainer ref={slideContainerRef}>
                    {galleryImages.map((image, index) => (
                        <Slide
                            key={image.id}
                            ref={(el) => {
                                slideRefs.current[index] = el;
                            }}
                        >
                            <SlideImageWrapper>
                                <Image
                                    src={{
                                        url: image.url,
                                        quality: 90,
                                        width: [600, 900, 1200, 1600, 2000],
                                    }}
                                    sizes={['100vw']}
                                    alt={image.title}
                                    title={image.title}
                                    objectFit="contain"
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                />
                            </SlideImageWrapper>
                        </Slide>
                    ))}
                </SlideContainer>

                {/* Product info overlay - bottom left */}
                <InfoOverlay>
                    <InfoText>
                        <Text typography={['Primary/24_110_510_-3', null, null, 'Primary/24_110_510_-3']} as="h1">
                            {productName}
                        </Text>
                        {text && (
                            <Text
                                typography={[
                                    'Secondary/Mobile/18_120_400_-1_Indent',
                                    null,
                                    null,
                                    'Secondary/24_120_400_-1_Indent',
                                ]}
                            >
                                {text}
                            </Text>
                        )}
                    </InfoText>
                </InfoOverlay>

                {/* Dot navigation - mobile only */}
                {galleryImages.length > 1 && (
                    <DotNav>
                        {galleryImages.map((image, index) => (
                            <Dot
                                key={image.id}
                                $isActive={index === activeIndex}
                                onClick={() => scrollToSlide(index)}
                                aria-label={`Go to image ${index + 1} of ${galleryImages.length}`}
                                aria-current={index === activeIndex ? 'true' : 'false'}
                                type="button"
                            />
                        ))}
                    </DotNav>
                )}

                {/* Thumbnail navigation - desktop only */}
                {galleryImages.length > 1 && (
                    <ThumbnailNav>
                        {visibleThumbnails.map((image) => (
                            <Thumbnail
                                key={image.id}
                                $isActive={image.originalIndex === activeIndex}
                                onClick={() => scrollToSlide(image.originalIndex)}
                                aria-label={`View image ${image.originalIndex + 1} of ${galleryImages.length}`}
                                aria-current={image.originalIndex === activeIndex ? 'true' : 'false'}
                                type="button"
                            >
                                <Image
                                    src={{
                                        url: image.url,
                                        quality: 90,
                                        width: [100, 200, 300],
                                    }}
                                    sizes={['50px']}
                                    alt={`Thumbnail ${image.originalIndex + 1}`}
                                    objectFit="cover"
                                    loading="lazy"
                                />
                            </Thumbnail>
                        ))}
                    </ThumbnailNav>
                )}
            </GalleryViewport>
        );
    },
);

ProductGallery.displayName = 'ProductGallery';

export default ProductGallery;
